import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy, where, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface SecurityLog {
  id: string;
  event: string;
  email: string;
  userId: string;
  status: 'success' | 'error';
  timestamp: Date | null;
  details?: unknown;
  error?: string;
}

type SortKey = 'timestamp' | 'event' | 'email' | 'userId' | 'status';

const SecurityLogs: React.FC = () => {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'timestamp',
    direction: 'desc'
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const logsRef = collection(db, 'authLogs');
      const logsQuery = query(logsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(logsQuery);

      const parsedLogs: SecurityLog[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate instanceof Function ? data.timestamp.toDate() : null;
        const hasError = Boolean(data.error);

        return {
          id: doc.id,
          event: String(data.event || 'Unknown'),
          email: String(data.email || ''),
          userId: String(data.userId || ''),
          status: hasError ? 'error' : 'success',
          timestamp,
          details: data.details,
          error: data.error ? String(data.error) : undefined
        };
      });

      setLogs(parsedLogs);
    } catch (err) {
      console.error('Error fetching security logs:', err);
      setError('Failed to load security logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleDeletePreviousYear = async () => {
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;
    const startOfPreviousYear = new Date(previousYear, 0, 1);
    const startOfCurrentYear = new Date(currentYear, 0, 1);

    const confirmed = window.confirm(
      `Delete all Security Logs from ${previousYear}? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeleting(true);
      setError('');
      setActionMessage('');

      const logsRef = collection(db, 'authLogs');
      const previousYearQuery = query(
        logsRef,
        where('timestamp', '>=', Timestamp.fromDate(startOfPreviousYear)),
        where('timestamp', '<', Timestamp.fromDate(startOfCurrentYear))
      );

      const snapshot = await getDocs(previousYearQuery);
      if (snapshot.empty) {
        setActionMessage(`No security logs found for ${previousYear}.`);
        return;
      }

      const docsToDelete = snapshot.docs;
      const batchSize = 400;

      for (let i = 0; i < docsToDelete.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = docsToDelete.slice(i, i + batchSize);
        chunk.forEach((docSnap) => batch.delete(docSnap.ref));
        await batch.commit();
      }

      setActionMessage(`Deleted ${docsToDelete.length} security logs from ${previousYear}.`);
      await fetchLogs();
    } catch (err) {
      console.error('Error deleting previous year security logs:', err);
      setError('Failed to delete previous year logs');
    } finally {
      setDeleting(false);
    }
  };

  const onSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedLogs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = logs.filter((log) => {
      if (!normalizedSearch) return true;

      return (
        log.event.toLowerCase().includes(normalizedSearch) ||
        log.email.toLowerCase().includes(normalizedSearch) ||
        log.userId.toLowerCase().includes(normalizedSearch) ||
        log.status.toLowerCase().includes(normalizedSearch)
      );
    });

    return filtered.sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      switch (sortConfig.key) {
        case 'timestamp': {
          const aTime = a.timestamp?.getTime() ?? 0;
          const bTime = b.timestamp?.getTime() ?? 0;
          return direction * (aTime - bTime);
        }
        case 'event':
          return direction * a.event.localeCompare(b.event);
        case 'email':
          return direction * a.email.localeCompare(b.email);
        case 'userId':
          return direction * a.userId.localeCompare(b.userId);
        case 'status':
          return direction * a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
  }, [logs, searchTerm, sortConfig]);

  if (loading) {
    return <div>Loading security logs...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="report-section">
      <div className="security-logs-header">
        <h2>Security Logs</h2>
        <button
          type="button"
          className="export-button security-delete-button"
          onClick={handleDeletePreviousYear}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete Previous Year Logs'}
        </button>
      </div>
      <p className="report-description">
        Authentication activity from the <code>authLogs</code> collection.
      </p>
      {actionMessage && <div className="security-action-message">{actionMessage}</div>}

      <div className="email-search-container">
        <input
          type="text"
          placeholder="Search by event, email, user ID, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="email-search-input"
        />
      </div>

      <div className="email-list-container">
        <table className="email-table">
          <thead>
            <tr>
              <th onClick={() => onSort('timestamp')}>
                Timestamp {sortConfig.key === 'timestamp' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => onSort('event')}>
                Event {sortConfig.key === 'event' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => onSort('email')}>
                Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => onSort('userId')}>
                User ID {sortConfig.key === 'userId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => onSort('status')}>
                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedLogs.length === 0 ? (
              <tr>
                <td colSpan={5} className="security-empty-state">
                  No security log entries found.
                </td>
              </tr>
            ) : (
              filteredAndSortedLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.timestamp ? log.timestamp.toLocaleString() : 'Unknown'}</td>
                  <td>{log.event}</td>
                  <td>{log.email || 'N/A'}</td>
                  <td>{log.userId || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${log.status === 'error' ? 'failed' : 'sent'}`}>
                      {log.status}
                    </span>
                    {log.error && <div className="security-error-text">{log.error}</div>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SecurityLogs;
